import React, { createContext, useContext, useState, useEffect } from 'react';
import { readSharedToken, clearSharedAuthTokens, persistSessionToken } from './utils/authTokenKeys';
import { classifyLoginResponse } from '../auth/loginResponse';
import { apiBase } from '../utils/authHeaders';

const MobileAuthContext = createContext();

export const useMobileAuth = () => {
  const context = useContext(MobileAuthContext);
  if (!context) {
    throw new Error('useMobileAuth must be used within MobileAuthProvider');
  }
  return context;
};

const USER_ROLES = {
  ADMIN: 'admin',
  DEPARTMENT_HEAD: 'department_head', 
  TEAM_MEMBER: 'team_member',
  STAFF: 'staff',
  DRIVER: 'driver'
};

// Server roles (users.role) → the four mobile role tiers this shell understands.
const SERVER_ROLE_TIER = {
  org_owner: USER_ROLES.ADMIN, org_admin: USER_ROLES.ADMIN, restaurant_owner: USER_ROLES.ADMIN,
  owner: USER_ROLES.ADMIN, owner_finance: USER_ROLES.ADMIN, admin: USER_ROLES.ADMIN,
  superadmin: USER_ROLES.ADMIN, platform_admin: USER_ROLES.ADMIN,
  manager: USER_ROLES.DEPARTMENT_HEAD, site_manager: USER_ROLES.DEPARTMENT_HEAD, ck_manager: USER_ROLES.DEPARTMENT_HEAD,
  shift_supervisor: USER_ROLES.DEPARTMENT_HEAD, food_safety_lead: USER_ROLES.DEPARTMENT_HEAD,
  department_head: USER_ROLES.DEPARTMENT_HEAD,
  chef: USER_ROLES.TEAM_MEMBER, head_chef: USER_ROLES.TEAM_MEMBER, team_member: USER_ROLES.TEAM_MEMBER,
  driver: USER_ROLES.DRIVER,
};

export function mobileRoleFor(serverRole) {
  return SERVER_ROLE_TIER[serverRole] || USER_ROLES.STAFF;
}

const ROLE_PERMISSIONS = {
  [USER_ROLES.ADMIN]: {
    canManageOrganizations: true,
    canManageUsers: true,
    canManageBilling: true,
    canManageSettings: true,
    canAccessAllFeatures: true,
    canViewAnalytics: true,
    canManageInventory: true,
    canManageBookings: true,
    canManageEvents: true,
    canManageDelivery: true,
    canManageSafety: true
  },
  [USER_ROLES.DEPARTMENT_HEAD]: {
    canManageOrganizations: false,
    canManageUsers: false,
    canManageBilling: false,
    canManageSettings: false,
    canAccessAllFeatures: false,
    canViewAnalytics: true,
    canManageInventory: true,
    canManageBookings: true,
    canManageEvents: true,
    canManageDelivery: true,
    canManageSafety: true
  },
  [USER_ROLES.TEAM_MEMBER]: {
    canManageOrganizations: false,
    canManageUsers: false,
    canManageBilling: false,
    canManageSettings: false,
    canAccessAllFeatures: false,
    canViewAnalytics: false,
    canManageInventory: true,
    canManageBookings: false,
    canManageEvents: false,
    canManageDelivery: true,
    canManageSafety: true
  },
  [USER_ROLES.STAFF]: {
    canManageOrganizations: false,
    canManageUsers: false,
    canManageBilling: false,
    canManageSettings: false,
    canAccessAllFeatures: false,
    canViewAnalytics: false,
    canManageInventory: false,
    canManageBookings: false,
    canManageEvents: false,
    canManageDelivery: false,
    canManageSafety: true
  },
  [USER_ROLES.DRIVER]: {
    canManageOrganizations: false,
    canManageUsers: false,
    canManageBilling: false,
    canManageSettings: false,
    canAccessAllFeatures: false,
    canViewAnalytics: false,
    canManageInventory: false,
    canManageBookings: false,
    canManageEvents: false,
    canManageDelivery: true,
    canManageSafety: false
  }
};

function persistInitialPlatformScope(data) {
  const organizations = Array.isArray(data.organizations) ? data.organizations : [];
  if (Array.isArray(data.organizations)) {
    localStorage.setItem('available_organizations', JSON.stringify(organizations));
  }

  const previousOrgId = localStorage.getItem('current_organization_id');
  const nextOrgId = data.default_organization?.id || (organizations.length === 1 ? organizations[0].id : '');
  if (nextOrgId && previousOrgId !== nextOrgId) {
    localStorage.setItem('current_organization_id', nextOrgId);
    localStorage.removeItem('current_location_id');
  } else if (!nextOrgId) {
    localStorage.removeItem('current_organization_id');
    localStorage.removeItem('current_location_id');
  }

  if (nextOrgId && data.default_location?.id && !localStorage.getItem('current_location_id')) {
    localStorage.setItem('current_location_id', data.default_location.id);
  }
}

export const MobileAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  // Cross-surface SSO: accept the desktop SPA token (`token`) as a fallback so a
  // user logged in via the desktop shell can navigate to /mobile/* without a
  // second login. Persist back as `auth_token` for the mobile flow's own use.
  // Shared with syncQueue.js's own token read via authTokenKeys.js — they'd
  // drifted into two different implementations before (audit-2026-09-07 §2).
  const [token, setToken] = useState(readSharedToken());
  const [role, setRole] = useState(USER_ROLES.STAFF);
  const [loading, setLoading] = useState(true);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    // Monitor network status
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    // Check auth state
    const initAuth = async () => {
      const storedToken = readSharedToken();
      if (storedToken) {
        try {
          // Verify token and get profile
          const response = await fetch(`${apiBase()}/api/auth/profile`, {
            headers: { 'Authorization': `Bearer ${storedToken}` }
          });
          
          if (response.ok) {
            const userProfile = await response.json();
            setUser(userProfile);
            setRole(mobileRoleFor(userProfile.role));
            setToken(storedToken);
            // One session for both shells: the desktop app and authHeaders() read `token`.
            try { persistSessionToken(storedToken); } catch { /* ignore */ }
          } else {
            // Token invalid or expired
            logout();
          }
        } catch (error) {
          // Offline — keep current token; mobile shell can continue with cached state
          console.warn('Error verifying token:', error);
          if (navigator.onLine) {
            logout();
          }
        }
      }
      setLoading(false);
    };

    initAuth();

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const login = async (email, password) => {
    let data;
    try {
      const response = await fetch(`${apiBase()}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Login failed');
      }

      data = await response.json();
    } catch (error) {
      throw new Error('Login failed: ' + error.message);
    }
    const step = classifyLoginResponse(data);
    if (step.kind === 'mfa') return { mfaRequired: true, mfaToken: step.mfaToken };
    if (step.kind === 'enrol') return { enrolmentRequired: true, mfaToken: step.mfaToken };
    if (step.kind === 'password_change') {
      throw new Error('Your password was set by an administrator. Sign in on the web once to choose your own, then use the app.');
    }
    return completeLogin(data);
  };

  // Shared by password login and two-factor verify; refuses anything without a real token.
  const completeLogin = async (data) => {
    if (classifyLoginResponse(data).kind !== 'session') throw new Error('Sign-in did not complete. Please try again.');
    const { token: newToken, user: userData } = data;
    persistInitialPlatformScope(data);
    persistSessionToken(newToken);
    setToken(newToken);
    setUser(userData);
    setRole(mobileRoleFor(userData?.role));
    return userData;
  };

  const logout = async () => {
    // Was: removeItem('auth_token') only. A stale `token` (or `mobileToken`)
    // key left behind would then shadow a LATER user's session on the same
    // shared/kiosk device — readSharedToken() prefers auth_token, but the
    // next mobile login only ever writes auth_token too, so a leftover
    // `token` from a prior desktop-SSO visit outlives this logout and never
    // gets cleared any other way. clearSharedAuthTokens() removes all three
    // (audit-2026-09-07 §2).
    clearSharedAuthTokens();
    setToken(null);
    setUser(null);
    setRole(USER_ROLES.STAFF);
  };

  const hasPermission = (permission) => {
    return ROLE_PERMISSIONS[role]?.[permission] || false;
  };

  const canAccessFeature = (feature) => {
    switch (feature) {
      case 'analytics':
        return hasPermission('canViewAnalytics');
      case 'inventory':
        return hasPermission('canManageInventory');
      case 'bookings':
        return hasPermission('canManageBookings');
      case 'events':
        return hasPermission('canManageEvents');
      case 'delivery':
        return hasPermission('canManageDelivery');
      case 'safety':
        return hasPermission('canManageSafety');
      case 'admin':
        return hasPermission('canManageOrganizations');
      default:
        return false;
    }
  };

  const getMobileNavigation = () => {
    const navigation = [
      {
        name: 'Dashboard',
        icon: 'home',
        path: '/mobile/dashboard',
        roles: [USER_ROLES.ADMIN, USER_ROLES.DEPARTMENT_HEAD]
      },
      {
        name: 'Inventory',
        icon: 'package',
        path: '/mobile/inventory',
        roles: [USER_ROLES.ADMIN, USER_ROLES.DEPARTMENT_HEAD, USER_ROLES.TEAM_MEMBER]
      },
      {
        name: 'Receive',
        icon: 'truck',
        path: '/mobile/receiving',
        roles: [USER_ROLES.ADMIN, USER_ROLES.DEPARTMENT_HEAD, USER_ROLES.TEAM_MEMBER]
      },
      {
        name: 'Invoices',
        icon: 'file-text',
        path: '/mobile/invoices',
        roles: [USER_ROLES.ADMIN, USER_ROLES.DEPARTMENT_HEAD]
      },
      {
        name: 'Scan',
        icon: 'scan',
        path: '/mobile/scan',
        roles: [USER_ROLES.ADMIN, USER_ROLES.DEPARTMENT_HEAD, USER_ROLES.TEAM_MEMBER, USER_ROLES.STAFF]
      },
      {
        name: 'Bookings',
        icon: 'calendar',
        path: '/mobile/bookings',
        roles: [USER_ROLES.ADMIN, USER_ROLES.DEPARTMENT_HEAD]
      },
      {
        name: 'Delivery',
        icon: 'truck',
        path: '/mobile/delivery',
        roles: [USER_ROLES.ADMIN, USER_ROLES.DEPARTMENT_HEAD, USER_ROLES.TEAM_MEMBER]
      },
      {
        name: 'Safety',
        icon: 'shield',
        path: '/mobile/safety',
        roles: [USER_ROLES.ADMIN, USER_ROLES.DEPARTMENT_HEAD, USER_ROLES.TEAM_MEMBER, USER_ROLES.STAFF]
      },
      {
        name: 'Events',
        icon: 'users',
        path: '/mobile/events',
        roles: [USER_ROLES.ADMIN, USER_ROLES.DEPARTMENT_HEAD]
      }
    ];

    return navigation.filter(item => item.roles.includes(role));
  };

  const value = {
    user,
    token,
    role,
    loading,
    isOnline,
    login,
    completeLogin,
    logout,
    hasPermission,
    canAccessFeature,
    getMobileNavigation,
    USER_ROLES,
    ROLE_PERMISSIONS
  };

  return (
    <MobileAuthContext.Provider value={value}>
      {children}
    </MobileAuthContext.Provider>
  );
};
